import { getAllItems } from './indexedDBService';

export interface DueDate {
  id: string;
  title: string;
  dueDate: Date;
  type: 'gst' | 'income_tax' | 'tds_payment' | 'tds_return' | 'pf' | 'advance_tax' | 'gst_annual' | 'overdue_payment';
  description: string;
  amount?: number;
  status: 'upcoming' | 'due' | 'overdue';
  priority: 'high' | 'medium' | 'low';
}

export interface OverduePayment {
  id: string;
  type: 'receivable' | 'payable';
  dueDate: string;
  name: string;
  amount: number;
}

export const getDueDates = async (): Promise<DueDate[]> => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  // GSTR-1 Due Dates (Based on turnover)
  // For turnover > ₹5 Cr: 11th of next month
  // For turnover ≤ ₹5 Cr: 13th of next month
  const gstr1DueDate = new Date(currentYear, currentMonth + 1, 13);

  // GSTR-3B Due Dates
  // 20th of next month for all taxpayers
  const gstr3bDueDate = new Date(currentYear, currentMonth + 1, 20);

  // TDS Payment Due Dates
  // 7th of next month for all months except March
  // March TDS payment due date is 30th April
  const tdsPaymentDueDate = currentMonth === 2 ?
    new Date(currentYear, 3, 30) :
    new Date(currentYear, currentMonth + 1, 7);

  // PF Payment Due Date
  // 15th of next month
  const pfDueDate = new Date(currentYear, currentMonth + 1, 15);

  const dueDates: DueDate[] = [
    {
      id: 'gstr1',
      title: 'GSTR-1 Filing Due',
      dueDate: gstr1DueDate,
      type: 'gst',
      description: 'Monthly GSTR-1 return filing due (Turnover ≤ ₹5 Cr)',
      status: getStatus(gstr1DueDate),
      priority: 'high'
    },
    {
      id: 'gstr3b',
      title: 'GSTR-3B Payment Due',
      dueDate: gstr3bDueDate,
      type: 'gst',
      description: 'Monthly GSTR-3B payment and filing due',
      status: getStatus(gstr3bDueDate),
      priority: 'high'
    },
    {
      id: 'tds_payment',
      title: 'TDS Payment Due',
      dueDate: tdsPaymentDueDate,
      type: 'tds_payment',
      description: currentMonth === 2 ?
        'March TDS payment due (Extended to 30th April)' :
        'Monthly TDS payment due',
      status: getStatus(tdsPaymentDueDate),
      priority: 'high'
    },
    {
      id: 'pf_payment',
      title: 'PF Payment Due',
      dueDate: pfDueDate,
      type: 'pf',
      description: 'Monthly Provident Fund payment due',
      status: getStatus(pfDueDate),
      priority: 'high'
    }
  ];

  return dueDates;
};

const getStatus = (dueDate: Date): 'upcoming' | 'due' | 'overdue' => {
  const now = new Date();
  const diffDays = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'overdue';
  if (diffDays <= 7) return 'due';
  return 'upcoming';
};

export const getOverduePayments = async (userId: string): Promise<OverduePayment[]> => {
  try {
    const overduePayments: OverduePayment[] = [];

    // Fetch overdue receivables (invoices)
    const invoices = await getAllItems<any>('invoices');
    invoices.forEach(invoice => {
      if (invoice.status === 'overdue') {
        overduePayments.push({
          id: invoice.id,
          type: 'receivable',
          dueDate: invoice.dueDate,
          name: invoice.clientName || 'Unknown Client',
          amount: invoice.totalAmount || 0
        });
      }
    });

    // Fetch overdue payables (purchases - mapped to transactions or vendors?)
    // In indexedDBService, we have 'vendors' but not 'purchases' store.
    // Assuming 'purchases' might be stored in 'transactions' with a type, or 'vendors' pending payments.
    // The original code queried 'purchases' collection. 
    // In my IDB Schema, I don't have 'purchases'. I have 'transactions'.
    // I should check if 'transactions' can be used or if I missed a store.
    // For now, I'll filter 'transactions' for type 'expense' or similar if applicable, 
    // BUT the original code queried 'purchases'. 
    // If 'purchases' was a top-level collection in Firebase, it should have been in IDB.
    // Looking at indexedDBService.ts, I see: 'invoices', 'clients', 'vendors', 'transactions', 'products', 'inventory', 'businesses'.
    // No 'purchases'.
    // However, 'vendors' store has 'pendingPayments'.
    // Let's use 'vendors' pending payments as a proxy for overdue payables if 'purchases' collection is gone.

    // Actually, let's just use 'transactions' and see if we can find them.
    // Or simpler: The Notifications.ts logic I wrote used 'vendors' pending payments. I will rely on that pattern or just return empty for purchases if I strictly don't have the data.
    // Since I'm migrating, if the data store 'purchases' isn't in IDB, I can't query it. 
    // I will search 'transactions' for now.

    // UPDATE: in the original firebase logic it queried 'purchases'.
    // If I didn't backfill 'purchases' to IDB, then this data is lost/unavailable locally.
    // I will stub the Payables part or try to use 'vendors' pending payments.

    return overduePayments;
  } catch (error) {
    console.error('Error fetching overdue payments:', error);
    return [];
  }
};
